import {
  BondRefundRow,
  CustomerInfo,
  HuulgaQuarter,
  HudaldanAvaltRow,
  QuarterHudaldanAvaltRow,
  StockRefundRow,
  TsenegleltRow,
  BuitsaaltRow,
} from "./types";

// Parser row types without year (year is added later by the hook)
type ParsedHudaldanAvaltRow = Omit<HudaldanAvaltRow, "year">;
type ParsedBuitsaaltRow = Omit<BuitsaaltRow, "year">;
type ParsedQuarterHudaldanAvaltRow = Omit<
  QuarterHudaldanAvaltRow,
  "year" | "quarter"
>;
type ParsedTsenegleltRow = Omit<
  TsenegleltRow,
  "year" | "quarter"
>;
type ParsedBondRefundRow = Omit<
  BondRefundRow,
  "year" | "quarter"
>;
type ParsedStockRefundRow = Omit<
  StockRefundRow,
  "year" | "quarter"
>;

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function parseCSVText(text: string): string[][] {
  const lines = text.split("\n").filter((l) => l.trim().length > 0);
  return lines.map(parseCSVLine);
}

function normalizeHeader(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[()]/g, "")
    .replace(/ё/g, "е")
    .trim();
}

function findHeaderRowIndex(
  rows: string[][],
  requiredHeaders: string[][]
): number {
  return rows.findIndex((row) => {
    const normalized = row.map(normalizeHeader);
    return requiredHeaders.every((variants) =>
      variants.some((variant) =>
        normalized.includes(normalizeHeader(variant))
      )
    );
  });
}

function getHeaderIndex(
  headers: string[],
  variants: string[]
): number {
  const normalizedHeaders = headers.map(normalizeHeader);
  for (const variant of variants) {
    const index = normalizedHeaders.indexOf(
      normalizeHeader(variant)
    );
    if (index !== -1) {
      return index;
    }
  }
  return -1;
}

function getCell(row: string[], index: number): string {
  if (index < 0) return "";
  return (row[index] || "").trim();
}

// Normalize date to MM/DD/YYYY HH:mm:ss format (preserving time)
export function formatDate(raw: string): string {
  if (!raw || !raw.trim()) return "";
  const s = raw.trim();

  // Try "YYYY/MM/DD HH:mm:ss" or "YYYY-MM-DD HH:mm:ss"
  const isoWithTime = s.match(
    /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})\s+(\d{1,2}:\d{2}:\d{2})/
  );
  if (isoWithTime) {
    const [, y, m, d, time] = isoWithTime;
    return `${parseInt(m)}/${parseInt(d)}/${y} ${time}`;
  }

  // Try "YYYY/MM/DD" or "YYYY-MM-DD" (no time)
  const isoNoTime = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (isoNoTime) {
    const [, y, m, d] = isoNoTime;
    return `${parseInt(m)}/${parseInt(d)}/${y}`;
  }

  // Try "MM/DD/YYYY HH:mm:ss"
  const usWithTime = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}:\d{2}:\d{2})/
  );
  if (usWithTime) {
    const [, m, d, y, time] = usWithTime;
    return `${parseInt(m)}/${parseInt(d)}/${y} ${time}`;
  }

  // Try "MM/DD/YYYY" (no time)
  const usNoTime = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usNoTime) {
    const [, m, d, y] = usNoTime;
    return `${parseInt(m)}/${parseInt(d)}/${y}`;
  }

  return s;
}

// Format a number to fixed decimal places
export function formatDecimal(value: string, places: number): string {
  const num = parseFloat(value.replace(/[,\s]/g, ""));
  if (isNaN(num)) return value;
  return num.toFixed(places);
}

// Format lockAmount: ₮xxx.xx
export function formatLockAmount(value: string): string {
  const num = parseFloat(value.replace(/[₮,\s]/g, ""));
  if (isNaN(num)) return value;
  return `₮${num.toFixed(2)}`;
}

export function parseNumberString(value: string): number {
  // Remove ₮ symbol and commas, then parse
  const cleaned = value.replace(/[₮,\s]/g, "");
  return Number(cleaned) || 0;
}

export function formatMoney(value: string): string {
  const num = parseNumberString(value);
  if (num === 0 && !value.trim()) return "";
  return `₮${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PURCHASE_FIXED_HEADER_VARIANTS = [
  "Огноо",
  "Овог",
  "Нэр",
  "Регистр",
  "Утас",
  "И-мейл",
  "Имэйл",
  "Email",
  "Гүйлгээний код",
  "Гүйлгээний утга",
  "Мөнгөн дүн",
  "Багц №",
  "Бонд нэр",
  "Bond нэр",
  "Бонд",
  "Estate",
  "Битүүмжлэгдсэн дүн",
];

export function parseNiitHudaldanAvaltCSV(text: string): {
  customers: Map<
    string,
    CustomerInfo & {
      registerNo: string;
      purchases: ParsedQuarterHudaldanAvaltRow[];
    }
  >;
  holdingKeys: string[];
} {
  const rows = parseCSVText(text);
  const customers = new Map<
    string,
    CustomerInfo & {
      registerNo: string;
      purchases: ParsedQuarterHudaldanAvaltRow[];
    }
  >();

  const headerIdx = findHeaderRowIndex(rows, [
    ["И-мейл", "Имэйл", "Email"],
    ["Мөнгөн дүн"],
    ["Багц №"],
  ]);
  if (headerIdx === -1) {
    return { customers, holdingKeys: [] };
  }

  const headers = rows[headerIdx];
  const lockAmountIdx = getHeaderIndex(headers, [
    "Битүүмжлэгдсэн дүн",
  ]);
  const dateTimeIdx = getHeaderIndex(headers, ["Огноо"]);
  const dateIdx = headers.findIndex(
    (_, index) =>
      index > dateTimeIdx &&
      normalizeHeader(headers[index] || "") ===
        normalizeHeader("Огноо")
  );
  const lastNameIdx = getHeaderIndex(headers, ["Овог"]);
  const firstNameIdx = getHeaderIndex(headers, ["Нэр"]);
  const registerIdx = getHeaderIndex(headers, ["Регистр"]);
  const phoneIdx = getHeaderIndex(headers, ["Утас"]);
  const emailIdx = getHeaderIndex(headers, [
    "И-мейл",
    "Имэйл",
    "Email",
  ]);
  const purposeIdx = getHeaderIndex(headers, [
    "Гүйлгээний код",
    "Гүйлгээний утга",
  ]);
  const amountIdx = getHeaderIndex(headers, ["Мөнгөн дүн"]);
  const batchNoIdx = getHeaderIndex(headers, ["Багц №"]);
  const bondNameIdx = getHeaderIndex(headers, [
    "Бонд нэр",
    "Bond нэр",
  ]);
  const bondIdx = getHeaderIndex(headers, ["Бонд"]);

  const holdingKeys = headers
    .map((header, index) => ({ header: header || "", index }))
    .filter(
      ({ header, index }) =>
        index > lockAmountIdx &&
        !!header.trim() &&
        !PURCHASE_FIXED_HEADER_VARIANTS.some(
          (variant) =>
            normalizeHeader(variant) === normalizeHeader(header)
        )
    )
    .map(({ header }) => header.trim());

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    const email = getCell(r, emailIdx).toLowerCase();
    if (!email || !email.includes("@")) continue;

    const holdings: Record<string, string> = {};
    holdingKeys.forEach((holdingKey) => {
      const idx = headers.findIndex(
        (header) =>
          normalizeHeader(header || "") ===
          normalizeHeader(holdingKey)
      );
      holdings[holdingKey] = getCell(r, idx);
    });

    const purchase: ParsedQuarterHudaldanAvaltRow = {
      key: `ha-quarter-${i}`,
      dateTime: formatDate(
        getCell(r, dateTimeIdx) || getCell(r, dateIdx)
      ),
      date: formatDate(
        getCell(r, dateIdx) || getCell(r, dateTimeIdx)
      ),
      amount: getCell(r, amountIdx)
        ? formatMoney(getCell(r, amountIdx))
        : "",
      batchNo: getCell(r, batchNoIdx),
      bondName: getCell(r, bondNameIdx),
      bond: getCell(r, bondIdx)
        ? formatMoney(getCell(r, bondIdx))
        : "",
      lockAmount: getCell(r, lockAmountIdx)
        ? formatLockAmount(getCell(r, lockAmountIdx))
        : "",
      holdings,
    };

    if (customers.has(email)) {
      customers.get(email)!.purchases.push(purchase);
    } else {
      customers.set(email, {
        email,
        register: getCell(r, registerIdx),
        lastName: getCell(r, lastNameIdx),
        firstName: getCell(r, firstNameIdx),
        phone: getCell(r, phoneIdx),
        registerNo: getCell(r, registerIdx),
        purchases: [purchase],
      });
    }
  }

  return { customers, holdingKeys };
}

export function parseNiitTsenegleltCSV(
  text: string
): Map<string, ParsedTsenegleltRow[]> {
  const rows = parseCSVText(text);
  const result = new Map<string, ParsedTsenegleltRow[]>();

  const headerIdx = findHeaderRowIndex(rows, [
    ["И-мейл", "Имэйл", "Email"],
    ["Мөнгөн дүн"],
    ["Тайлбар"],
  ]);
  if (headerIdx === -1) {
    return result;
  }

  const headers = rows[headerIdx];
  const dateIdx = getHeaderIndex(headers, ["Огноо"]);
  const lastNameIdx = getHeaderIndex(headers, ["Овог"]);
  const firstNameIdx = getHeaderIndex(headers, ["Нэр"]);
  const registerIdx = getHeaderIndex(headers, ["Регистр"]);
  const phoneIdx = getHeaderIndex(headers, ["Утас"]);
  const emailIdx = getHeaderIndex(headers, [
    "И-мейл",
    "Имэйл",
    "Email",
  ]);
  const purposeIdx = getHeaderIndex(headers, [
    "Гүйлгээний код",
    "Гүйлгээний утга",
  ]);
  const amountIdx = getHeaderIndex(headers, ["Мөнгөн дүн"]);
  const descriptionIdx = getHeaderIndex(headers, ["Тайлбар"]);
  const depositDateIdx = getHeaderIndex(headers, [
    "Цэнэглэсэн огноо",
  ]);

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    const email = getCell(r, emailIdx).toLowerCase();
    if (!email || !email.includes("@")) continue;

    const entry: ParsedTsenegleltRow = {
      key: `ts-quarter-${i}`,
      date: formatDate(getCell(r, dateIdx)),
      lastName: getCell(r, lastNameIdx),
      firstName: getCell(r, firstNameIdx),
      register: getCell(r, registerIdx),
      phone: getCell(r, phoneIdx),
      email,
      purpose: getCell(r, purposeIdx),
      amount: getCell(r, amountIdx)
        ? formatMoney(getCell(r, amountIdx))
        : "",
      description: getCell(r, descriptionIdx),
      depositDate: formatDate(getCell(r, depositDateIdx)),
    };

    if (result.has(email)) {
      result.get(email)!.push(entry);
    } else {
      result.set(email, [entry]);
    }
  }

  return result;
}

export function parseBondRefundCSV(
  text: string
): Map<string, ParsedBondRefundRow[]> {
  const parsed = parseCSVText(text);
  const result = new Map<string, ParsedBondRefundRow[]>();

  const headerIdx = findHeaderRowIndex(parsed, [
    ["Email"],
    ["Хөрөнгийн төрөл"],
    ["Шилжүүлэх дүн"],
  ]);
  if (headerIdx === -1) {
    return result;
  }

  const headers = parsed[headerIdx];
  const emailIdx = getHeaderIndex(headers, ["Email"]);
  const phoneIdx = getHeaderIndex(headers, ["Утасны дугаар"]);
  const lastNameIdx = getHeaderIndex(headers, ["Овог"]);
  const firstNameIdx = getHeaderIndex(headers, ["Нэр"]);
  const registerIdx = getHeaderIndex(headers, [
    "Регистрийн дугаар",
  ]);
  const accountIdx = getHeaderIndex(headers, ["Данс"]);
  const bankNameIdx = getHeaderIndex(headers, [
    "Хүлээн авах банк",
  ]);
  const purposeIdx = getHeaderIndex(headers, [
    "Гүйлгээний утга",
  ]);
  const ttdIdx = getHeaderIndex(headers, ["ТТД"]);
  const assetNameIdx = getHeaderIndex(headers, [
    "Хөрөнгийн төрөл",
  ]);
  const principalIdx = getHeaderIndex(headers, ["Мөнгөн дүн"]);
  const rateIdx = getHeaderIndex(headers, ["Бонд хүү"]);
  const purchaseDateIdx = getHeaderIndex(headers, [
    "Худалдан авсан огноо",
  ]);
  const calcDateIdx = getHeaderIndex(headers, [
    "Хүү тооцоолсон огноо",
  ]);
  const daysIdx = getHeaderIndex(headers, [
    "Хүү тооцох хоног",
  ]);
  const calcInterestIdx = getHeaderIndex(headers, [
    "Тооцсон хүү",
  ]);
  const interestThirtyPercentIdx = headers.findIndex(
    (header) => normalizeHeader(header || "") === "0.3"
  );
  const totalIdx = getHeaderIndex(headers, ["Нийт"]);
  const avtamataFeeIdx = getHeaderIndex(headers, [
    "AvtaMata шимтгэл",
  ]);
  const tradingFeeIdx = getHeaderIndex(headers, [
    "Арилжааны шимтгэл",
  ]);
  const taxIdx = getHeaderIndex(headers, ["ОАТ"]);
  const transferAmountIdx = getHeaderIndex(headers, [
    "Шилжүүлэх дүн",
  ]);

  for (let i = headerIdx + 1; i < parsed.length; i++) {
    const r = parsed[i];
    const email = getCell(r, emailIdx).toLowerCase();
    if (!email || !email.includes("@")) continue;

    const row: ParsedBondRefundRow = {
      key: `bond-refund-${i}`,
      email,
      phone: getCell(r, phoneIdx),
      lastName: getCell(r, lastNameIdx),
      firstName: getCell(r, firstNameIdx),
      register: getCell(r, registerIdx),
      account: getCell(r, accountIdx),
      bankName: getCell(r, bankNameIdx),
      purpose: getCell(r, purposeIdx),
      ttd: getCell(r, ttdIdx),
      assetName: getCell(r, assetNameIdx),
      principal: getCell(r, principalIdx)
        ? formatMoney(getCell(r, principalIdx))
        : "",
      bondInterestRate: getCell(r, rateIdx),
      purchaseDate: formatDate(getCell(r, purchaseDateIdx)),
      calcDate: formatDate(getCell(r, calcDateIdx)),
      days: getCell(r, daysIdx),
      calcInterest: getCell(r, calcInterestIdx)
        ? formatMoney(getCell(r, calcInterestIdx))
        : "",
      interestThirtyPercent: getCell(
        r,
        interestThirtyPercentIdx
      )
        ? formatMoney(getCell(r, interestThirtyPercentIdx))
        : "",
      total: getCell(r, totalIdx)
        ? formatMoney(getCell(r, totalIdx))
        : "",
      avtamataFee: getCell(r, avtamataFeeIdx)
        ? formatMoney(getCell(r, avtamataFeeIdx))
        : "",
      tradingFee: getCell(r, tradingFeeIdx)
        ? formatMoney(getCell(r, tradingFeeIdx))
        : "",
      tax: getCell(r, taxIdx)
        ? formatMoney(getCell(r, taxIdx))
        : "",
      transferAmount: getCell(r, transferAmountIdx)
        ? formatMoney(getCell(r, transferAmountIdx))
        : "",
    };

    if (result.has(email)) {
      result.get(email)!.push(row);
    } else {
      result.set(email, [row]);
    }
  }

  return result;
}

export function parseStockRefundCSV(
  text: string
): Map<string, ParsedStockRefundRow[]> {
  const parsed = parseCSVText(text);
  const result = new Map<string, ParsedStockRefundRow[]>();

  const headerIdx = findHeaderRowIndex(parsed, [
    ["Email"],
    ["Хувьцааны нэр"],
    ["Шилжүүлэх дүн"],
  ]);
  if (headerIdx === -1) {
    return result;
  }

  const headers = parsed[headerIdx];
  const emailIdx = getHeaderIndex(headers, ["Email"]);
  const phoneIdx = getHeaderIndex(headers, ["Утасны дугаар"]);
  const lastNameIdx = getHeaderIndex(headers, ["Овог"]);
  const firstNameIdx = getHeaderIndex(headers, ["Нэр"]);
  const registerIdx = getHeaderIndex(headers, [
    "Регистрийн дугаар",
  ]);
  const accountIdx = getHeaderIndex(headers, ["Данс"]);
  const bankNameIdx = getHeaderIndex(headers, [
    "Хүлээн авах банк",
  ]);
  const purposeIdx = getHeaderIndex(headers, [
    "Гүйлгээний утга",
  ]);
  const ttdIdx = getHeaderIndex(headers, ["ТТД"]);
  const assetTypeIdx = getHeaderIndex(headers, [
    "Хөрөнгийн төрөл",
  ]);
  const purchaseDateIdx = getHeaderIndex(headers, [
    "Худалдан авсан огноо",
  ]);
  const stockNameIdx = getHeaderIndex(headers, [
    "Хувьцааны нэр",
  ]);
  const quantityIdx = getHeaderIndex(headers, ["Тоо ширхэг"]);
  const stockPriceIdx = getHeaderIndex(headers, [
    "Хувьцааны ханш",
  ]);
  const usdRateIdx = getHeaderIndex(headers, ["Доллар ханш"]);
  const grossTotalIdx = getHeaderIndex(headers, ["Нийт Үнэ"]);
  const tradingFeeIdx = getHeaderIndex(headers, [
    "Арилжааны шимтгэл",
  ]);
  const avtamataFeeIdx = getHeaderIndex(headers, [
    "Автамата шимтгэл",
  ]);
  const transferAmountIdx = getHeaderIndex(headers, [
    "Шилжүүлэх дүн",
  ]);

  for (let i = headerIdx + 1; i < parsed.length; i++) {
    const r = parsed[i];
    const email = getCell(r, emailIdx).toLowerCase();
    if (!email || !email.includes("@")) continue;

    const row: ParsedStockRefundRow = {
      key: `stock-refund-${i}`,
      email,
      phone: getCell(r, phoneIdx),
      lastName: getCell(r, lastNameIdx),
      firstName: getCell(r, firstNameIdx),
      register: getCell(r, registerIdx),
      account: getCell(r, accountIdx),
      bankName: getCell(r, bankNameIdx),
      purpose: getCell(r, purposeIdx),
      ttd: getCell(r, ttdIdx),
      assetType: getCell(r, assetTypeIdx),
      purchaseDate: formatDate(getCell(r, purchaseDateIdx)),
      stockName: getCell(r, stockNameIdx),
      quantity: getCell(r, quantityIdx),
      stockPrice: getCell(r, stockPriceIdx),
      usdRate: getCell(r, usdRateIdx),
      grossTotal: getCell(r, grossTotalIdx)
        ? formatMoney(getCell(r, grossTotalIdx))
        : "",
      tradingFee: getCell(r, tradingFeeIdx)
        ? formatMoney(getCell(r, tradingFeeIdx))
        : "",
      avtamataFee: getCell(r, avtamataFeeIdx)
        ? formatMoney(getCell(r, avtamataFeeIdx))
        : "",
      transferAmount: getCell(r, transferAmountIdx)
        ? formatMoney(getCell(r, transferAmountIdx))
        : "",
    };

    if (result.has(email)) {
      result.get(email)!.push(row);
    } else {
      result.set(email, [row]);
    }
  }

  return result;
}

// ХА Нийт CSV: columns are Огноо, Огноо, Овог, Нэр, Регистр, Утас, И-мейл, Мөнгөн дүн, Багц №, Bond, Estate, Битүүмжлэгдсэн дүн, FX, DIA, VDE, VUG, VXF, ...
export function parseHaNiitCSV(text: string): {
  customers: Map<
    string,
    CustomerInfo & { purchases: ParsedHudaldanAvaltRow[]; registerNo: string }
  >;
} {
  const rows = parseCSVText(text);
  const customers = new Map<
    string,
    CustomerInfo & { purchases: ParsedHudaldanAvaltRow[]; registerNo: string }
  >();

  // Skip header (row 0)
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.length < 12) continue;
    const email = (r[6] || "").toLowerCase().trim();
    if (!email || !email.includes("@")) continue;

    const purchase: ParsedHudaldanAvaltRow = {
      key: `ha-${i}`,
      date: formatDate(r[0] || r[1] || ""),
      amount: r[7] ? formatMoney(r[7]) : "",
      batchNo: r[8] || "",
      bond: r[9] ? formatMoney(r[9]) : "",
      estate: r[10] ? formatMoney(r[10]) : "",
      lockAmount: r[11] ? formatLockAmount(r[11]) : "",
      fx: r[12] ? formatDecimal(r[12], 4) : "",
      dia: r[13] ? formatDecimal(r[13], 4) : "",
      vde: r[14] ? formatDecimal(r[14], 4) : "",
      vug: r[15] ? formatDecimal(r[15], 4) : "",
      vxf: r[16] ? formatDecimal(r[16], 4) : "",
    };

    if (customers.has(email)) {
      customers.get(email)!.purchases.push(purchase);
    } else {
      customers.set(email, {
        email,
        register: r[4] || "",
        lastName: r[2] || "",
        firstName: r[3] || "",
        phone: r[5] || "",
        registerNo: r[4] || "",
        purchases: [purchase],
      });
    }
  }

  return { customers };
}

// Цэнэглэлт CSV: Огноо, Овог, Нэр, Регистр, Утас, И-мейл, Багц №, Мөнгөн дүн, Тайлбар, Цэнэглэсэн огноо
export function parseTseneglelCSV(
  text: string
): Map<string, { date: string; depositDate: string; description: string; amount: string }[]> {
  const rows = parseCSVText(text);
  const result = new Map<
    string,
    { date: string; depositDate: string; description: string; amount: string }[]
  >();

  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.length < 10) continue;
    const email = (r[5] || "").toLowerCase().trim();
    if (!email || !email.includes("@")) continue;

    const entry = {
      date: formatDate(r[0] || ""),
      depositDate: formatDate(r[9] || ""),
      description: r[8] || "",
      amount: r[7] || "",
    };

    if (result.has(email)) {
      result.get(email)!.push(entry);
    } else {
      result.set(email, [entry]);
    }
  }

  return result;
}

// Хуулга CSV: Голомт, Огноо, КТ/Зарлага, ДТ/Орлого, Гүйлгээний код, account, note, ...
export function parseHuulgaCSV(text: string): {
  rows: {
    date: string;
    expense: string;
    income: string;
    code: string;
    account: string;
  }[];
} {
  const parsed = parseCSVText(text);
  const rows: {
    date: string;
    expense: string;
    income: string;
    code: string;
    account: string;
  }[] = [];

  for (let i = 1; i < parsed.length; i++) {
    const r = parsed[i];
    if (r.length < 5) continue;
    const date = r[0] || "";
    if (!date) continue;

    rows.push({
      date: r[1] || r[0] || "",
      expense: r[2] || "",
      income: r[3] || "",
      code: r[4] || "",
      account: r[5] || "",
    });
  }

  return { rows };
}

// Зарлага CSV: Голомт, КТ/Зарлага, Гүйлгээний код, (empty), Дансны нэр, Код, Хэзээ цэнэглэсэн
export function parseZarlagaCSV(text: string): {
  rows: { date: string; amount: string; code: string; account: string }[];
} {
  const parsed = parseCSVText(text);
  const rows: { date: string; amount: string; code: string; account: string }[] = [];

  for (let i = 1; i < parsed.length; i++) {
    const r = parsed[i];
    if (r.length < 4) continue;
    const date = r[0] || "";
    if (!date) continue;

    rows.push({
      date: formatDate(r[0]),
      amount: r[1] || "",
      code: r[2] || "",
      account: r[3] || "",
    });
  }

  return { rows };
}

// Буцаалт CSV: (empty row), header, data rows
// Header: Email, Утасны дугаар, Овог, Нэр, Регистрийн дугаар, Данс, Хүлээн авах банк, Гүйлгээний утга, ТТД, Хөрөнгийн төрөл, Мөнгөн дүн, Бонд хүү, Худалдан авсан огноо, Хүү тооцоолсон огноо, Хүү тооцох хоног, Тооцсон хүү, 0.3, Нийт, AvtaMata шимтгэл, Арилжааны шимтгэл, ОАТ, Шилжүүлэх дүн, Хүүний зөрүү ашиг, Автамата шимтгэл
export function parseBuitsaaltCSV(
  text: string
): Map<string, ParsedBuitsaaltRow[]> {
  const parsed = parseCSVText(text);
  const result = new Map<string, ParsedBuitsaaltRow[]>();

  // Find header row (contains "Email")
  let headerIdx = -1;
  for (let i = 0; i < parsed.length; i++) {
    if (parsed[i].some((c) => c.toLowerCase() === "email")) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) return result;

  for (let i = headerIdx + 1; i < parsed.length; i++) {
    const r = parsed[i];
    if (r.length < 22) continue;
    const email = (r[0] || "").toLowerCase().trim();
    if (!email || !email.includes("@")) continue;

    const row: ParsedBuitsaaltRow = {
      key: `bu-${i}`,
      calcDate: formatDate(r[13] || ""),
      assetName: r[9] || "",
      principal: r[10] ? formatMoney(r[10]) : "",
      bondInterest: r[11] || "",
      days: r[14] || "",
      calcInterest: r[15] ? formatMoney(r[15]) : "",
      total: r[17] ? formatMoney(r[17]) : "",
      avtamataFee: r[18] ? formatMoney(r[18]) : "",
      tradingFee: r[19] ? formatMoney(r[19]) : "",
      tax: r[20] ? formatMoney(r[20]) : "",
      transferAmount: r[21] ? formatMoney(r[21]) : "",
      account: r[5] || "",
    };

    if (result.has(email)) {
      result.get(email)!.push(row);
    } else {
      result.set(email, [row]);
    }
  }

  return result;
}
